import { Capacitor, registerPlugin } from '@capacitor/core';
import { datadogRum } from '@datadog/browser-rum';

/** Which Auth0 tenant the shell opened its session against. Only ever two. */
export type NativeAuthTenant = 'dev' | 'prod';

/** A flow the shell can carry out itself. Anything absent keeps running the web app's own flow. */
export type NativeAuthMethod = 'otp' | 'ulp' | 'sso';

export interface NativeAuthAvailability {
    available: boolean;
    tenant: NativeAuthTenant;
    methods?: NativeAuthMethod[];
}

/** What the shell reports about the session. Never carries the refresh token, by design. */
export interface NativeAuthSession {
    authenticated: boolean;
    accessToken?: string;
    expiresAt?: number;
    userId?: string;
    givenName?: string;
    familyName?: string;
    email?: string;
    idTokenExpiresAt?: number;
}

export interface NativeLoginOptions {
    connection?: string;
    loginHint?: string;
    prompt?: string;
}

interface NativeAuthPlugin {
    isAvailable(): Promise<NativeAuthAvailability>;
    getSession(): Promise<NativeAuthSession>;
    getAccessToken(options: { force: boolean }): Promise<NativeAuthSession>;
    sendOtp(options: { phoneNumber: string }): Promise<{ sent: boolean }>;
    verifyOtp(options: { phoneNumber: string; code: string }): Promise<NativeAuthSession>;
    login(options: NativeLoginOptions): Promise<NativeAuthSession>;
    beginWebLogin(options: {
        redirectUri: string;
        prompt?: string;
    }): Promise<{ authorizeUrl: string }>;
    logout(): Promise<NativeAuthSession>;
    addListener(
        event: 'authChanged',
        handler: (session: NativeAuthSession) => void
    ): Promise<{ remove: () => Promise<void> }>;
}

let plugin: NativeAuthPlugin | undefined;

// Registered on first use so importing this module stays free of side effects.
const nativeAuth = (): NativeAuthPlugin => {
    plugin ??= registerPlugin<NativeAuthPlugin>('NativeAuth');
    return plugin;
};

// Undefined in a browser and on a shell that predates the plugin: both keep the web app's own auth.
export async function readNativeAuthAvailability(): Promise<NativeAuthAvailability | undefined> {
    if (!Capacitor.isNativePlatform()) {
        return undefined;
    }
    try {
        return await nativeAuth().isAvailable();
    } catch (error) {
        datadogRum.addError(error, { context: 'readNativeAuthAvailability' });
        return undefined;
    }
}

/** Thin wrappers so the plugin handle stays private to this module. */
export const nativeGetSession = () => nativeAuth().getSession();

export const nativeGetAccessToken = (force: boolean) => nativeAuth().getAccessToken({ force });

export const nativeSendOtp = (phoneNumber: string) => nativeAuth().sendOtp({ phoneNumber });

export const nativeVerifyOtp = (phoneNumber: string, code: string) =>
    nativeAuth().verifyOtp({ phoneNumber, code });

export const nativeLogin = (options: NativeLoginOptions) => nativeAuth().login(options);

export const nativeLogout = () => nativeAuth().logout();

export const onNativeAuthChanged = (handler: (session: NativeAuthSession) => void) =>
    nativeAuth().addListener('authChanged', handler);

/**
 * Universal Login rendered in this WebView rather than a Custom Tab. The shell keeps the PKCE
 * verifier and answers the callback navigation itself, so the code never passes through here.
 */
export const beginNativeWebLogin = (redirectUri: string, prompt?: string) =>
    nativeAuth().beginWebLogin({ redirectUri, prompt });
